import React, { useState, useCallback } from "react";
import moment from "moment";
import "../../styles/request_dashboard.scss";

// Types

interface RequestInfoSidebarProps {
    data: {
        requestId?: string;
        requestType?: string;
        status?: number;
        chainName?: string;
        stores?: string | string[];
        requestDescription?: string;
        requestedBy?: string;
        email?: string;
        role?: string;
        requestRaisedOn?: string;
        requestedStore?: string;
        requestedChain?: string;
    };
    selectedAdmin: string;
    onOpenAssignModal: () => void;
    onApprove: () => void;
    isProcessing: boolean;
}

const RequestInfoSidebar: React.FC<RequestInfoSidebarProps> = ({
    data,
    selectedAdmin,
    onApprove,
    isProcessing,
}) => {
    const [isApproveHovered, setIsApproveHovered] = useState(false);
    const getStatusLabel = (statusCode: string | number | undefined) => {
        if (!statusCode) return "-";
        return statusCode
            .toString()
            .toLowerCase()
            .replace(/_/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };
    const storeChainInfo = data?.stores && data?.chainName;
    
    const handleApproveMouseEnter = useCallback(() => {
        if (!isProcessing) setIsApproveHovered(true);
    }, [isProcessing]);

    const handleApproveMouseLeave = useCallback(() => {
        setIsApproveHovered(false);
    }, []);

    const formatStores = (stores: string | string[] | undefined) => {
        if (!stores) return "-";
        const storeList = Array.isArray(stores)
            ? stores.filter((s) => s != null).map((s) => s.trim()).filter(Boolean)
            : stores.split(",").filter((s) => s != null).map((s) => s.trim()).filter(Boolean);
        if (storeList.length === 0) return "-";
        if (storeList.length <= 4) return storeList.join(", ");
        return storeList.slice(0, 4).join(", ") + "...";
    };

    const approveButtonStyle: React.CSSProperties = {
        backgroundColor: isApproveHovered && !isProcessing ? "#D4A610" : "#E5B42F",
        color: "#ffffff",
        border: "none",
        borderRadius: "6px",
        padding: "8px 16px",
        fontSize: "13px",
        fontWeight: 600,
        cursor: isProcessing ? "not-allowed" : "pointer",
        opacity: isProcessing ? 0.6 : 1,
        width: "100%",
        marginTop: "16px",
        transition: "background-color 0.2s ease",
    };

    return (
        <div className="ris-container">
            <h4 style={{
                fontSize: "13px",
                fontWeight: 600,
                color: "#111827",
                marginBottom: "12px",
                marginTop: 0,
            }}>
                Ticket Information
            </h4>

            {/* Request ID */}
            <div className="ris-info-group">
                <span className="ris-info-label">Request ID</span>
                <span className="ris-info-value">{data?.requestId || "-"}</span>
            </div>

            {/* Request Type */}
            <div className="ris-info-group">
                <span className="ris-info-label">Request Type</span>
                <span className="ris-info-value">{getStatusLabel(data?.requestType) || "-"}</span>
            </div>

            {/* Status */}
            <div className="ris-info-group">
                <span className="ris-info-label">Status</span>
                <span className="ris-info-value">{getStatusLabel(data?.status)}</span>
            </div>

            {/* Chain + Store — always same row */}
            <div className="ris-info-row">
                <div className="ris-info-group">
                    <span className="ris-info-label">Request Chain</span>
                    <span className="ris-info-value">
                        {data?.chainName ? data?.chainName : data?.requestedChain || "-"}
                    </span>
                </div>

                <div className="ris-info-group">
                    <span className="ris-info-label">Request Store</span>
                    <span className="ris-info-value">
                        {storeChainInfo ? formatStores(data?.stores) : data?.requestedStore || "-"}
                    </span>
                </div>
            </div>

            {/* Chain Description (conditional) */}
            {!storeChainInfo && (
                <div className="ris-info-group">
                    <span className="ris-info-label">Request Chain Description</span>
                    <span className="ris-info-value">{data?.requestDescription || "-"}</span>
                </div>
            )}

            <div style={{ height: 1, backgroundColor: "#e5e7eb", margin: "12px 0" }} />

            {/* Requested By */}
            <div className="ris-info-group">
                <span className="ris-info-label">Requested By</span>
                <span className="ris-info-value">{data?.requestedBy || "-"}</span>
            </div>

            {/* Email */}
            <div className="ris-info-group">
                <span className="ris-info-label">Email</span>
                <span className="ris-info-value">{data?.email || "-"}</span>
            </div>

            <div style={{ height: 1, backgroundColor: "#e5e7eb", margin: "12px 0" }} />

            {/* Request Raised On */}
            <div className="ris-info-group">
                <span className="ris-info-label">Request Raised on</span>
                <span className="ris-info-value">
                    {moment(data?.requestRaisedOn).format("MM-DD-YYYY")}
                </span>
            </div>

            {/* Approve Button */}
            {selectedAdmin !== "all" && data?.status === 1 && (
                <button
                    className="ris-approve-btn"
                    style={approveButtonStyle}
                    onClick={onApprove}
                    disabled={isProcessing}
                    onMouseEnter={handleApproveMouseEnter}
                    onMouseLeave={handleApproveMouseLeave}
                    aria-label={isProcessing ? "Processing request" : "Approve request"}
                >
                    {isProcessing ? "Processing..." : "Approve"}
                </button>
            )}
        </div>
    );
};

export default RequestInfoSidebar;